import { createStore, createEvent, createEffect, combine } from 'effector';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from '../pagination';
import createPreloadersStore from '../preloaders';
import createFiltersStore from '../filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import IUser from '@interfaces/IUser';
import { unblockUser } from '../users';
import localeWeightSpamUsers, { LOCALE } from '@stores/spamUsers/localeWeightSpamUsers';
import type { UserBlockReason } from '@constants/userBlockOptions';

interface ISpamUsersPrefetchedPage {
  page: number;
  pageSize: number;
  total: number;
  items: IUser[];
  cacheKey: string;
}

interface ISpamUsersPrefetchRequest {
  page: number | null;
  pageSize: number;
  cacheKey: string;
  isLoading: boolean;
}

const buildSpamUsersCacheKey = ({
  filters,
  locale,
  pageSize,
}: {
  filters: Record<string, any>;
  locale?: string;
  pageSize?: number;
}) =>
  JSON.stringify({
    ...filters,
    locale,
    pageSize,
  });

const getSpamUsersLocaleValue = () =>
  localeWeightSpamUsers.getState() === LOCALE.ALL ? undefined : localeWeightSpamUsers.getState();

const getCurrentSpamUsersCacheKey = (pageSize?: number) =>
  buildSpamUsersCacheKey({
    filters: {
      ...defaultFilters,
      ...filters.getState(),
    },
    locale: getSpamUsersLocaleValue(),
    pageSize,
  });

const isRelevantPrefetchedNextPage = (pageData: ISpamUsersPrefetchedPage | null) => {
  if (!pageData) {
    return false;
  }

  const { current, pageSize } = pagination.getState();

  return (
    pageData.page === (current || 1) + 1 &&
    pageData.pageSize === pageSize &&
    pageData.cacheKey === getCurrentSpamUsersCacheKey(pageSize) &&
    pageData.items.length > 0
  );
};

export const isCurrentPrefetchRequest = (
  requestData: ISpamUsersPrefetchRequest | null,
  page: number,
  pageSize?: number,
  cacheKey?: string,
) =>
  !!requestData &&
  requestData.isLoading &&
  requestData.page === page &&
  requestData.pageSize === pageSize &&
  requestData.cacheKey === cacheKey;

export const reset = createEvent('Spam store reset');

export const clearPrefetchedPage = createEvent('Clear prefetched spam users page');
export const showPrefetchedPage = createEvent<IUser[]>('Show prefetched spam users page');
export const resetFastPrefetch = createEvent('Reset fast spam users prefetch');

export const DEFAULT_SPAM_USERS_SORT = [
  'registrationDate,desc',
  'accountClaimsCount,desc',
  'claimDate,desc',
];

export const DEFAULT_SPAM_USERS_FILTERS = {
  checkedType: 'UNCHECKED',
  showBlocked: false,
  hideUnblocked: false,
  sort: DEFAULT_SPAM_USERS_SORT,
};

const defaultFilters = {
  ...DEFAULT_SPAM_USERS_FILTERS,
};

export const reject = createEffect({
  name: 'reject',
  async handler({ userId }: { userId: IUser['id']; preloaderId?: string }) {
    const response = await request(api.spam.complainView, {
      urlParams: { id: userId },
    });
    return response;
  },
});

export const block = createEffect({
  name: 'blockUser',
  async handler({
    userId,
    daysToUnblock,
    daysToClean,
    reason,
  }: {
    userId: IUser['id'];
    preloaderId: string;
    daysToUnblock?: number;
    daysToClean?: number;
    reason?: UserBlockReason;
  }) {
    const response = await request(api.users.block, {
      urlParams: { userId },
      params: {
        daysToUnblock,
        daysToClean,
        reason,
      },
    });

    await request(api.spam.complainView, {
      urlParams: { id: userId },
    });

    return response;
  },
});

export const getList = createEffect({
  name: 'getList',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: Record<string, any>;
    sort?: Array<string>;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();

    const sorterParams: { sort?: Array<string> } = {};

    if (params?.filters) {
      setFilters(params.filters);
      filtersParams = params.filters;
      paginationParams.current = 1;
    }

    if (params?.sort) {
      sorterParams.sort = params.sort;
      setFilters({ ...filtersParams, ...sorterParams });
      filtersParams = { ...filtersParams, ...sorterParams };
    }

    const { current, pageSize } = paginationParams;

    const response = await request(api.spam.getUsers, {
      params: {
        paged: true,
        size: pageSize,
        page: (current || 1) - 1,
        ...removeUndefinedFilters({
          ...defaultFilters,
          ...filtersParams,
          locale:
            localeWeightSpamUsers.getState() === LOCALE.ALL
              ? undefined
              : localeWeightSpamUsers.getState(),
        }),
      },
    });

    return response;
  },
});

export const prefetchPage = createEffect({
  name: 'prefetchSpamUsersPage',
  async handler(page: number) {
    const paginationParams: PaginationProps = pagination.getState();
    const filtersParams = filters.getState();
    const { pageSize } = paginationParams;

    const localeValue =
      localeWeightSpamUsers.getState() === LOCALE.ALL
        ? undefined
        : localeWeightSpamUsers.getState();

    const response = await request(api.spam.getUsers, {
      params: {
        paged: true,
        size: pageSize,
        page: page - 1,
        ...removeUndefinedFilters({
          ...defaultFilters,
          ...filtersParams,
          locale: localeValue,
        }),
      },
    });

    return {
      page,
      pageSize: pageSize || 0,
      total: response.data.body.totalElements,
      items: response.data.body.content,
      cacheKey: buildSpamUsersCacheKey({
        filters: {
          ...defaultFilters,
          ...filtersParams,
        },
        locale: localeValue,
        pageSize,
      }),
    };
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'spamUsers',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, reject, block, prefetchPage]);

const { filters, setFilters } = createFiltersStore({
  listName: 'spamUsers',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const prefetchedPageRequest = createStore<ISpamUsersPrefetchRequest | null>(null)
  .on(prefetchPage, (_, page) => {
    const pageSize = pagination.getState().pageSize || 0;

    return {
      page,
      pageSize,
      cacheKey: getCurrentSpamUsersCacheKey(pageSize),
      isLoading: true,
    };
  })
  .on(prefetchPage.done, (state) => {
    if (!state) {
      return null;
    }

    return {
      ...state,
      isLoading: false,
    };
  })
  .on(prefetchPage.fail, (state) => {
    if (!state) {
      return null;
    }

    return {
      ...state,
      isLoading: false,
    };
  })
  .on(clearPrefetchedPage, () => null)
  .on(getList, () => null)
  .reset(reset);

const shouldPrefetchNextPageFast = createStore(false)
  .on(getList.done, () => true)
  .on(prefetchPage.done, () => false)
  .on(resetFastPrefetch, () => false)
  .on(clearPrefetchedPage, () => false)
  .reset(reset);

const fillListFromPrefetchedPage = (state: IUser[], removedUserId: IUser['id']) => {
  const nextState = state.filter((user) => user.id !== removedUserId);
  const pageSize = pagination.getState().pageSize;
  const currentPrefetchedPage = prefetchedPage.getState();

  if (!currentPrefetchedPage || !isRelevantPrefetchedNextPage(currentPrefetchedPage)) {
    getList();
    return nextState;
  }

  const nextUser = currentPrefetchedPage.items[0];

  if (!nextUser || nextState.some((user) => user.id === nextUser.id)) {
    return nextState;
  }

  setPagination({
    ...pagination.getState(),
    total: Math.max((pagination.getState().total || 0) - 1, 0),
  });

  return [...nextState, nextUser].slice(0, pageSize || nextState.length + 1);
};

const prefetchedPage = createStore<ISpamUsersPrefetchedPage | null>(null)
  .on(prefetchPage.done, (_, { result }) => {
    const currentPageSize = pagination.getState().pageSize;

    if (
      result.pageSize !== currentPageSize ||
      result.cacheKey !== getCurrentSpamUsersCacheKey(result.pageSize)
    ) {
      return null;
    }

    return result;
  })
  .on(clearPrefetchedPage, () => null)
  .on(getList, () => null)
  .on(reject.done, (state) => {
    if (!state || !isRelevantPrefetchedNextPage(state)) {
      return null;
    }

    return {
      ...state,
      total: Math.max(state.total - 1, 0),
      items: state.items.slice(1),
    };
  })
  .on(unblockUser.done, () => null)
  .on(block.done, (state) => {
    if (!state || !isRelevantPrefetchedNextPage(state)) {
      return null;
    }

    return {
      ...state,
      total: Math.max(state.total - 1, 0),
      items: state.items.slice(1),
    };
  })
  .reset(reset);

const list = createStore<IUser[]>([])
  .on(getList.done, (_, { result }) => result.data.body.content)
  .on(showPrefetchedPage, (_, payload: IUser[]) => payload)
  .on(reject.done, (state, { params }) => fillListFromPrefetchedPage(state, params.userId))
  .on(unblockUser.done, () => {
    getList();
  })
  .on(block.done, (state, { params }) => fillListFromPrefetchedPage(state, params.userId))
  .reset(reset);

const spamUsers = combine({
  preloaders,
  list,
  pagination,
  filters,
  prefetchedPage,
  prefetchedPageRequest,
  shouldPrefetchNextPageFast,
  setPagination,
});

export default spamUsers;
